import type { CommandPaletteGroup, CommandPaletteItem } from './CommandPalette'

/** A half-open [start, end) span of an item's label that matched the query. */
export type CommandMatchRange = readonly [number, number]

export type CommandFilter = 'records' | 'actions' | 'ask' | null

export interface ParsedCommandQuery {
  query: string
  filter: CommandFilter
  prefix: '@' | '>' | '?' | null
}

const INTERROGATIVE = /^(how|why|who|when|where|which|what|can|should)\b/iu

/** Prefixes narrow an already-admitted provider set; they never grant one. */
export function parseCommandQuery(raw: string): ParsedCommandQuery {
  const value = raw.trimStart()
  const prefix = value[0] === '@' || value[0] === '>' || value[0] === '?' ? value[0] : null
  return {
    query: prefix ? value.slice(1).trimStart() : raw,
    prefix,
    filter: prefix === '@' ? 'records' : prefix === '>' ? 'actions' : prefix === '?' ? 'ask' : null,
  }
}

export function commandIntent(query: string, actionLabels: readonly string[] = []): 'records' | 'actions' | 'ask' {
  const clean = query.trim()
  if (clean.endsWith('?') || INTERROGATIVE.test(clean)) return 'ask'
  const lower = clean.toLocaleLowerCase()
  if (actionLabels.some((label) => lower.startsWith(label.toLocaleLowerCase().split(/\s+/u)[0] ?? ''))) return 'actions'
  return 'records'
}

/* ── the help-aware intent table (#2502 §5.6, briefing 04 §5, palette.js) ────
 *
 * A host that supplies `help`, `tours` or `suggested` groups opts into the
 * portal's table, where the query's SHAPE decides which section leads:
 *
 * | query shape                                   | order                          |
 * |-----------------------------------------------|--------------------------------|
 * | empty                                         | Suggested · Go · Help · Do · Tours |
 * | a question (how/why/what/…, or a trailing ?)  | Help (Ask last) · Tours · Go · Do |
 * | tour / show me / guide / walk                 | Tours · Help · Go · Do         |
 * | a verb (open/request/share/invite/mark/…)     | Do · Go · Help · Tours         |
 * | anything else                                 | Go · Help · Do · Tours         |
 *
 * Go is `pages` then `records`; Do is `actions`. A host that supplies none of
 * the three new kinds — the console today — keeps the table above this block
 * EXACTLY (`commandRanking.test.ts` pins it against a frozen copy).
 */

export type CommandLead = 'suggested' | 'help' | 'tours' | 'do' | 'go'

/** What the leading section says about why it leads (palette.js copy). Go leads by default and says nothing. */
export const COMMAND_LEAD_REASON: Readonly<Record<'help' | 'tours' | 'do', string>> = {
  help: 'You asked a question',
  tours: 'You asked for a tour',
  do: 'Starts with an action',
}

const QUESTION = /^(?:how|why|what|where|when|who|which|can|could|should|does|do i|do we|is|are)\b/iu
const TOUR_WORDS = /\b(?:tours?|show me|guided?|walk(?:through)?)\b/iu
const VERB = /^(?:open|request|share|send|invite|add|report|mark|capture|create|new)\b/iu
const QUESTION_LEAD = /^(?:how (?:do|can|should) (?:i|we)|how to|how|why|what(?:'s| is| are)?|where(?: is| are)?|when|who|which|can (?:i|we)|do (?:i|we)|does|is|are|show me|walk me through|tour(?: of)?|guided?)\s+/iu

/** Which section leads for this query, under the help-aware table. */
export function commandLead(query: string, actionLabels: readonly string[] = []): CommandLead {
  const clean = query.trim()
  if (!clean) return 'suggested'
  if (clean.endsWith('?') || QUESTION.test(clean)) return 'help'
  if (TOUR_WORDS.test(clean)) return 'tours'
  const lower = clean.toLocaleLowerCase()
  if (VERB.test(clean)
    || actionLabels.some((label) => lower.startsWith(label.toLocaleLowerCase().split(/\s+/u)[0] || '\u0000'))) return 'do'
  return 'go'
}

/** The words that are matched: question words and a trailing `?` stripped ("how do I add seats" → "add seats"). */
export function commandMatchTerm(query: string): string {
  let term = query.trim()
  for (let previous = ''; previous !== term;) {
    previous = term
    term = term.replace(QUESTION_LEAD, '').replace(/\?+$/u, '').trim()
  }
  return term
}

/**
 * Where `term` occurs in `text`, as half-open [start, end) ranges for an
 * underline: the whole term when it occurs, otherwise each word's first
 * occurrence. Case-insensitive; empty when there is nothing to mark.
 */
export function commandMatchRanges(text: string, term: string): CommandMatchRange[] {
  const needle = term.trim().toLocaleLowerCase()
  const haystack = text.toLocaleLowerCase()
  if (!needle || haystack.length !== text.length) return []
  const whole = haystack.indexOf(needle)
  if (whole >= 0) return [[whole, whole + needle.length]]
  const ranges: CommandMatchRange[] = []
  for (const word of needle.split(/\s+/u)) {
    const at = haystack.indexOf(word)
    if (word && at >= 0) ranges.push([at, at + word.length])
  }
  ranges.sort((a, b) => a[0] - b[0] || a[1] - b[1])
  const merged: CommandMatchRange[] = []
  for (const range of ranges) {
    const last = merged.at(-1)
    if (last && range[0] <= last[1]) merged[merged.length - 1] = [last[0], Math.max(last[1], range[1])]
    else merged.push(range)
  }
  return merged
}

const HELP_AWARE_KINDS = new Set(['help', 'tours', 'suggested'])
const GO: readonly string[] = ['pages', 'records']
const DO: readonly string[] = ['actions']
const LEAD_ORDER: Readonly<Record<CommandLead, readonly string[]>> = {
  suggested: ['suggested', ...GO, 'help', ...DO, 'tours', 'ask', 'recall'],
  help: ['help', 'ask', 'tours', ...GO, ...DO, 'recall'],
  tours: ['tours', 'help', ...GO, ...DO, 'ask', 'recall'],
  do: [...DO, ...GO, 'help', 'tours', 'ask', 'recall'],
  go: [...GO, 'help', ...DO, 'tours', 'ask', 'recall'],
}
const LEAD_KINDS: Readonly<Record<'help' | 'tours' | 'do', readonly string[]>> = {
  help: ['help'], tours: ['tours'], do: DO,
}

function sortByKind(groups: readonly CommandPaletteGroup[], order: readonly string[]): CommandPaletteGroup[] {
  const rank = new Map(order.map((kind, index) => [kind, index]))
  return [...groups].sort((a, b) => (rank.get(a.kind ?? 'recall') ?? 99) - (rank.get(b.kind ?? 'recall') ?? 99))
}

function marked(item: CommandPaletteItem, term: string): CommandPaletteItem {
  const matches = commandMatchRanges(item.label, term)
  return matches.length ? { ...item, matches } : item
}

function rankHelpAware(query: string, groups: readonly CommandPaletteGroup[], actionLabels: readonly string[]): CommandPaletteGroup[] {
  const lead = commandLead(query, actionLabels)
  const present = lead === 'suggested' ? groups : groups.filter((group) => group.kind !== 'suggested')
  let ordered = sortByKind(present, LEAD_ORDER[lead])

  /* On a question, Ask is kept and ranks after the articles: its rows become
     the last rows of the (last) Help section rather than a section of its own. */
  const helpIndex = ordered.map((group) => group.kind).lastIndexOf('help')
  if (lead === 'help' && helpIndex >= 0) {
    const ask = ordered.filter((group) => group.kind === 'ask')
    if (ask.length) {
      const help = ordered[helpIndex]!
      const merged = { ...help, items: [...help.items, ...ask.flatMap((group) => group.items)] }
      ordered = ordered.flatMap((group, index) => index === helpIndex ? [merged] : group.kind === 'ask' ? [] : [group])
    }
  }

  const reasonKinds = lead === 'help' || lead === 'tours' || lead === 'do' ? LEAD_KINDS[lead] : []
  const reasonAt = ordered.findIndex((group) => reasonKinds.includes(group.kind ?? ''))
  const term = commandMatchTerm(query)
  return ordered.map((group, index) => {
    const items = term ? group.items.map((item) => marked(item, term)) : group.items
    const reason = index === reasonAt && lead !== 'suggested' && lead !== 'go' ? COMMAND_LEAD_REASON[lead] : undefined
    return reason || items !== group.items ? { ...group, items, ...(reason ? { reason } : {}) } : group
  })
}

/** Pure presentation order. Admission, permission and data access happened before this call. */
export function rankCommandGroups(
  query: string,
  groups: readonly CommandPaletteGroup[],
  filter: CommandFilter = null,
  actionLabels: readonly string[] = [],
): CommandPaletteGroup[] {
  if (filter) return groups.filter((group) => group.kind === filter)
  if (groups.some((group) => HELP_AWARE_KINDS.has(group.kind ?? ''))) return rankHelpAware(query, groups, actionLabels)
  const first = commandIntent(query, actionLabels)
  const order = first === 'ask'
    ? ['ask', 'actions', 'records', 'pages', 'recall']
    : first === 'actions'
      ? ['actions', 'records', 'pages', 'ask', 'recall']
      : ['records', 'actions', 'pages', 'ask', 'recall']
  return sortByKind(groups, order)
}
